import { CSSProperties, memo, useMemo, useEffect, useState } from 'react';

export const EMBEDDED_EVENT = 'compare-quick-nav-embedded-change';

interface ICompareQuickNavEmbeddedEventDetail {
    embedded?: boolean;
}

interface ISnapshotContentProps {
    isVisible: boolean;
    children: React.ReactNode;
    applyMinimalPadding?: boolean;
    isMergeApp?: boolean;
    isCompareApp?: boolean;
    isUnderwritingReviewApp?: boolean;
}

/**
 * Wrapper component for main content area
 * Handles responsive padding and visibility
 * Memoized for performance
 */
export const SnapshotContent = memo((props: ISnapshotContentProps) => {

    const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
    const [isEmbedded, setIsEmbedded] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setViewportWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const handleCompareQuickNavEmbeddedChange = (event: Event) => {
            const customEvent = event as CustomEvent<ICompareQuickNavEmbeddedEventDetail>;
            setIsEmbedded(customEvent.detail?.embedded === true);
        };

        window.addEventListener(EMBEDDED_EVENT, handleCompareQuickNavEmbeddedChange);

        return () => {
            window.removeEventListener(EMBEDDED_EVENT, handleCompareQuickNavEmbeddedChange);
        };
    }, []);

    const styles: CSSProperties = useMemo(() => {

        if (isEmbedded)
            return {
                paddingLeft: '0px',
                paddingRight: '0px',
                marginTop: '0px'
            };

        if (viewportWidth < 720)
            return {
                paddingLeft: '0px',
                paddingRight: '0px',
                marginTop: '10px'
            };

        if (viewportWidth < 1200 && (props.isMergeApp || props.isCompareApp))
            return {
                paddingLeft: '4px',
                paddingRight: '4px',
                marginTop: '10px'
            };

        if (props.applyMinimalPadding)
            return {
                paddingLeft: '16px',
                paddingRight: '16px',
                marginTop: '10px'
            };

        return {
            paddingLeft: '48px',
            paddingRight: '48px',
            marginTop: '10px'
        };

    }, [
        viewportWidth,
        props.applyMinimalPadding,
        props.isMergeApp,
        props.isCompareApp,
        isEmbedded
    ]);

    if (!props.isVisible) {
        return null;
    }

    return (
        <div style={styles}>
            {props.children}
        </div>
    );
});

SnapshotContent.displayName = 'SnapshotContent';

export default SnapshotContent;
